import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePortalDropdown } from '../../hooks/usePortalDropdown';
import styles from './PropertySearch.module.css';

export interface PropertyOption {
  id: string;
  name: string;
}

// The options list never grows past this, even with room to spare; the hook
// tightens it further when the viewport is short.
const LIST_MAX_HEIGHT = 240;

interface PropertySearchProps {
  available: PropertyOption[];
  emptyLabel: string;
  onSelect: (propertyId: string) => void;
  onClose: () => void;
}

export const PropertySearch = ({ available, emptyLabel, onSelect, onClose }: PropertySearchProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  // The parent mounts this component already-open, so the hook closing (outside
  // click, Escape) means the whole picker should go away, not just its panel.
  const { isOpen, position, panelStyle, triggerRef, panelRef, open, close } =
    usePortalDropdown<HTMLDivElement>({ onClose, maxPanelHeight: LIST_MAX_HEIGHT });

  useEffect(() => {
    inputRef.current?.focus();
    open();
  }, [open]);

  const trimmed = query.trim();

  const filtered = useMemo(() => {
    if (trimmed.length === 0) return available;
    const q = trimmed.toLowerCase();
    return available.filter((p) => p.name.toLowerCase().includes(q));
  }, [trimmed, available]);

  const showEmptyState = trimmed.length > 0 && filtered.length === 0;
  const showDropdown = isOpen && (filtered.length > 0 || showEmptyState);

  // No re-measure here: filtering changes the panel's height only once the new
  // list has rendered, and the hook's ResizeObserver picks that up. Measuring
  // from this handler would place the panel from the previous list's height.
  const handleQueryChange = (value: string): void => setQuery(value);

  return (
    <div ref={triggerRef} className={styles.wrapper}>
      <div className={styles.inputBox}>
        <input
          ref={inputRef}
          className={styles.input}
          type="text"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') close();
          }}
        />
      </div>
      {showDropdown &&
        createPortal(
          <div
            ref={panelRef}
            className={styles.dropdown}
            style={{ ...panelStyle, minWidth: position.width }}
          >
            {showEmptyState ? (
              <div className={`${styles.item} ${styles.emptyState}`}>
                {emptyLabel}
              </div>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.id}
                  className={styles.item}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onSelect(p.id)}
                >
                  {p.name}
                </button>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  );
};
